import {
  useOrganisationBankAccounts,
  useUpdateOrganisationAdditionalCurrencies,
} from '@my/directory-api/hooks';
import { CURRENCIES, findCurrency } from '@my/platform/options';
import { ConfirmDialog, FormSection, SelectInput, showToast } from '@my/ui';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseAdditionalCurrencyCodes } from './lib/currencyPlanning';

export interface CurrenciesSectionProps {
  /** Owning organisation id; the panel stays disabled while this is null. */
  organisationId: string | null;
  /** Org's base currency ISO code — rendered with a BASE badge, not removable. */
  baseCurrencyIsoCode: string;
  /**
   * Comma-separated additional currency ISO codes from the org payload
   * (`org.additionalCurrencyIsoCodes`). The component parses, normalises,
   * and dedupes — no upstream cleanup needed.
   */
  additionalCurrencyIsoCodes: string | null;
}

/**
 * Bottom-row card on the Organisation page. Shows the org's base currency
 * (with a `BASE` badge) plus any additional currencies, and lets operators
 * add / edit / remove additionals via the dedicated atomic endpoint.
 *
 * Editor pattern matches the rest of the page: the form always lives at the
 * top of the card, whether adding or editing — the row being edited is
 * hidden from the list below so there's one representation at a time. The
 * editor's footer carries trash + cancel + save (no trash when adding).
 */
export function CurrenciesSection({
  organisationId,
  baseCurrencyIsoCode,
  additionalCurrencyIsoCodes,
}: CurrenciesSectionProps) {
  const { t } = useTranslation('organisation');
  const updateMutation = useUpdateOrganisationAdditionalCurrencies();
  // Same query the Bank Accounts card runs — React Query dedupes by key, so
  // the second hook here doesn't fire an extra request.
  const bankAccountsQuery = useOrganisationBankAccounts(organisationId);

  // Parse the comma-separated string. Uppercased + deduped + base-excluded
  // for safety, even though the BE already does this on save. Shared helper
  // so the page-level preview and this card stay in lock-step.
  const additionals = useMemo<string[]>(
    () => parseAdditionalCurrencyCodes(additionalCurrencyIsoCodes, baseCurrencyIsoCode),
    [additionalCurrencyIsoCodes, baseCurrencyIsoCode],
  );

  // `null` = no editor open; `'new'` = drafting a brand-new row; `<code>` =
  // editing the additional currency with that ISO code.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draftCode, setDraftCode] = useState('');
  // Code pending a confirmation modal — `null` while the dialog is closed.
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  // Blocked-removal state: code attempted to remove + count of bank accounts
  // using it. Drives the read-only "this is in use" modal.
  const [blockedRemove, setBlockedRemove] = useState<{ code: string; count: number } | null>(null);

  // Currencies the user can pick in the editor:
  //  - master list MINUS base MINUS already-added additionals
  //  - PLUS the row's current code if we're editing one (so it shows up as
  //    the default selection and the user can keep it unchanged).
  const editorOptions = useMemo(() => {
    const taken = new Set<string>([
      baseCurrencyIsoCode.toUpperCase(),
      ...additionals.map((c) => c.toUpperCase()),
    ]);
    if (editingId && editingId !== 'new') {
      // Free up the row's current code so the dropdown can preselect it.
      taken.delete(editingId.toUpperCase());
    }
    return CURRENCIES.filter((c) => !taken.has(c.isoCode)).map((c) => ({
      value: c.isoCode,
      label: `${c.name} (${c.isoCode})`,
    }));
  }, [additionals, baseCurrencyIsoCode, editingId]);

  const startAdd = () => {
    setEditingId('new');
    setDraftCode(editorOptions[0]?.value ?? '');
  };

  const startEdit = (code: string) => {
    setEditingId(code);
    setDraftCode(code);
  };

  const cancel = () => {
    setEditingId(null);
    setDraftCode('');
  };

  const save = async () => {
    if (!organisationId || editingId === null) return;
    const next = draftCode.trim().toUpperCase();
    if (!next) {
      showToast({ type: 'error', message: t('currencies.pickCurrency') });
      return;
    }
    let codes: string[];
    if (editingId === 'new') {
      codes = [...additionals, next];
    } else if (editingId === next) {
      // Editing without changing the value — nothing to save.
      cancel();
      return;
    } else {
      // Swap: drop the original, append the new value.
      codes = additionals.filter((c) => c !== editingId).concat(next);
    }
    try {
      await updateMutation.mutateAsync({ organisationId, codes });
      showToast({
        type: 'success',
        message:
          editingId === 'new'
            ? t('currencies.added', { code: next })
            : t('currencies.updated', { code: next }),
      });
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('currencies.saveFailed'),
      });
    }
  };

  // Stage a code for confirmation; the actual removal fires from
  // `confirmDeletePending` once the operator clicks Delete in the modal.
  // If any bank accounts still use the currency, surface a blocking
  // dialog instead — currencies in use cannot be removed until the
  // dependents are detached first.
  const requestDelete = (code: string) => {
    if (!organisationId) return;
    const count = (bankAccountsQuery.data ?? []).filter(
      (b) => b.currencyIsoCode.toUpperCase() === code.toUpperCase(),
    ).length;
    if (count > 0) {
      setBlockedRemove({ code, count });
      return;
    }
    setPendingDelete(code);
  };

  const confirmDeletePending = async () => {
    const code = pendingDelete;
    if (!code || !organisationId) return;
    try {
      await updateMutation.mutateAsync({
        organisationId,
        codes: additionals.filter((c) => c !== code),
      });
      showToast({ type: 'success', message: t('currencies.removed', { code }) });
      if (editingId === code) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('currencies.removeFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // Hide the +Add header button while the editor is open or there's nothing
  // left to add — same pattern as BankAccountsSection.
  const headerAction =
    editingId === null && editorOptions.length > 0 ? (
      <button
        type="button"
        aria-label={t('currencies.addAria')}
        onClick={startAdd}
        disabled={!organisationId || updateMutation.isPending}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('currencies.add')}
      </button>
    ) : null;

  /**
   * Editor body — same JSX whether we render at the top (for `+Add`) or
   * in-place (replacing a row when editing). Skips the trash button when
   * adding, since there's nothing to delete yet.
   */
  const renderEditor = () => (
    <div className="flex items-end gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      {editingId !== null && editingId !== 'new' && (
        <div className="pb-1">
          <button
            type="button"
            aria-label={t('currencies.ariaRemoveCurrency')}
            onClick={() => requestDelete(editingId)}
            disabled={updateMutation.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="flex-1">
        <SelectInput
          label={t('currencies.currencyLabel')}
          name="currencyDraft"
          value={draftCode}
          onChange={(e) => setDraftCode(e.target.value)}
          options={editorOptions}
        />
      </div>
      <div className="flex items-center gap-1 pb-1">
        <button
          type="button"
          aria-label={t('currencies.ariaCancel')}
          onClick={cancel}
          disabled={updateMutation.isPending}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label={t('currencies.ariaSaveCurrency')}
          onClick={save}
          disabled={updateMutation.isPending || !draftCode}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <FormSection title={t('currencies.title')} columns={1} actions={headerAction}>
      <ul className="space-y-2">
        {/*
          New-currency draft renders at the TOP of the list so the user's
          attention follows the +Add click straight to the editor. Edits to
          existing rows render IN-PLACE further down — see below. The
          editor JSX paints its own bordered slate-50 panel, so the wrapping
          <li> doesn't add another frame.
        */}
        {editingId === 'new' && <li>{renderEditor()}</li>}

        <CurrencyRow code={baseCurrencyIsoCode} kind="base" />

        {additionals.map((code) =>
          editingId === code ? (
            <li key={code}>{renderEditor()}</li>
          ) : (
            <CurrencyRow
              key={code}
              code={code}
              kind="additional"
              onEdit={() => startEdit(code)}
              editDisabled={updateMutation.isPending}
            />
          ),
        )}
      </ul>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('currencies.deleteDialog.title')}
        message={
          pendingDelete ? t('currencies.deleteDialog.message', { code: pendingDelete }) : null
        }
        confirmLabel={t('currencies.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={updateMutation.isPending}
      />

      {/*
        Read-only "this is in use" modal. Both buttons close it (we treat
        it as informational); the confirm label is `Got it` rather than
        the destructive `Remove` so it can't be misclicked into a delete.
        Plural-aware via i18next's `_one` / `_other` suffixes — the count
        drives which key is selected automatically.
      */}
      <ConfirmDialog
        open={blockedRemove !== null}
        tone="danger"
        title={t('currencies.inUseDialog.title')}
        message={
          blockedRemove
            ? t('currencies.inUseDialog.message', {
                count: blockedRemove.count,
                code: blockedRemove.code,
              })
            : null
        }
        confirmLabel={t('currencies.inUseDialog.confirm')}
        cancelLabel={t('currencies.inUseDialog.close')}
        onConfirm={() => setBlockedRemove(null)}
        onCancel={() => setBlockedRemove(null)}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────

interface CurrencyRowProps {
  code: string;
  kind: 'base' | 'additional';
  onEdit?: () => void;
  editDisabled?: boolean;
}

function CurrencyRow({ code, kind, onEdit, editDisabled }: CurrencyRowProps) {
  const { t } = useTranslation('organisation');
  const meta = findCurrency(code);
  // Name on top in dark, then `Code: XYZ · Symbol: …` underneath. For
  // unknown codes (master-list miss) we show the bare code as the heading
  // and a single-line "Code: XYZ" caption.
  const heading = meta?.name ?? code;
  const caption = meta
    ? t('currencies.codeWithSymbol', { code: meta.isoCode, symbol: meta.symbol })
    : t('currencies.codeOnly', { code });
  return (
    <li className="flex items-center gap-2 rounded-sm border border-slate-200 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{heading}</p>
        <p className="truncate text-xs text-slate-500">{caption}</p>
      </div>
      {kind === 'base' ? (
        <span className="inline-flex items-center rounded-sm bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-inset ring-purple-200">
          {t('currencies.baseBadge')}
        </span>
      ) : (
        <button
          type="button"
          aria-label={t('currencies.ariaEdit', { code })}
          onClick={onEdit}
          disabled={editDisabled}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}
